import React, { useState, useEffect } from 'react';
import { Settings, Edit, Save, X, Plus, Trash2, Info } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import './SettingsManagement.css';
import { Base_Url } from '../config/domainConfig';

const API_BASE_URL = `${Base_Url}/api`;

const SettingsManagement = () => {
  const { showSuccess, showError } = useToast();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSetting, setEditingSetting] = useState(null);
  const [editForm, setEditForm] = useState({
    setting_value: '',
    setting_description: ''
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    setting_key: '',
    setting_value: '',
    setting_description: '',
    setting_type: 'string'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/settings`);
      const data = await response.json();

      if (data.success) {
        setSettings(data.data);
      } else {
        showError(data.message || 'Failed to fetch settings');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      showError('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (setting) => {
    setEditingSetting(setting);
    setEditForm({
      setting_value: setting.setting_value,
      setting_description: setting.setting_description || ''
    });
  };

  const handleSave = async () => {
    try {
      if (!editForm.setting_value.trim()) {
        showError('Setting value is required');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/settings/${editingSetting.setting_key}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editForm),
      });

      const data = await response.json();

      if (data.success) {
        showSuccess('Setting updated successfully!');
        setEditingSetting(null);
        fetchSettings();
      } else {
        showError(data.message || 'Failed to update setting');
      }
    } catch (error) {
      console.error('Error updating setting:', error);
      showError('Failed to update setting');
    }
  };

  const handleCancel = () => {
    setEditingSetting(null);
    setEditForm({ setting_value: '', setting_description: '' });
  };

  const handleAddSetting = async () => {
    try {
      if (!addForm.setting_key.trim() || !addForm.setting_value.trim()) {
        showError('Setting key and value are required');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(addForm),
      });

      const data = await response.json();

      if (data.success) {
        showSuccess('Setting created successfully!');
        setShowAddModal(false);
        setAddForm({
          setting_key: '',
          setting_value: '',
          setting_description: '',
          setting_type: 'string'
        });
        fetchSettings();
      } else {
        showError(data.message || 'Failed to create setting');
      }
    } catch (error) {
      console.error('Error creating setting:', error);
      showError('Failed to create setting');
    }
  };

  const handleDeleteSetting = async (setting) => {
    if (!window.confirm(`Are you sure you want to delete the setting "${setting.setting_key}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/settings/${setting.setting_key}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        showSuccess('Setting deleted successfully!');
        fetchSettings();
      } else {
        showError(data.message || 'Failed to delete setting');
      }
    } catch (error) {
      console.error('Error deleting setting:', error);
      showError('Failed to delete setting');
    }
  };

  const getTypeBadge = (type) => {
    const typeColors = {
      string: 'blue',
      number: 'green',
      boolean: 'orange',
      json: 'purple'
    };

    return (
      <span className={`type-badge ${typeColors[type] || 'gray'}`}>
        {type}
      </span>
    );
  };

  const getEditableBadge = (editable) => {
    return (
      <span className={`editable-badge ${editable ? 'editable' : 'non-editable'}`}>
        {editable ? 'Editable' : 'Read-only'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="settings-management">
        <div className="loading">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="settings-management">
      <div className="section-header">
        <h2>System Settings</h2>
        <button 
          className="btn-primary"
          onClick={() => setShowAddModal(true)}
        >
          <Plus size={16} />
          Add Setting
        </button>
      </div>

      <div className="settings-grid">
        {settings.map(setting => (
          <div key={setting.id} className="setting-card">
            <div className="setting-header">
              <div className="setting-key">
                <h3>{setting.setting_key}</h3>
                {getTypeBadge(setting.setting_type)}
                {getEditableBadge(setting.is_editable)}
              </div>
              <div className="setting-actions">
                {setting.is_editable && editingSetting?.id !== setting.id && (
                  <button 
                    className="btn-icon"
                    onClick={() => handleEdit(setting)}
                    title="Edit setting"
                  >
                    <Edit size={16} />
                  </button>
                )}
                {setting.is_editable && (
                  <button 
                    className="btn-icon danger"
                    onClick={() => handleDeleteSetting(setting)}
                    title="Delete setting"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>

            <div className="setting-content">
              {editingSetting?.id === setting.id ? (
                <div className="edit-form">
                  <div className="form-group">
                    <label>Value:</label>
                    <input
                      type={setting.setting_type === 'number' ? 'number' : 'text'}
                      value={editForm.setting_value}
                      onChange={(e) => setEditForm({...editForm, setting_value: e.target.value})}
                      placeholder={`Enter ${setting.setting_type} value`}
                    />
                  </div>
                  <div className="form-group">
                    <label>Description:</label>
                    <textarea
                      value={editForm.setting_description}
                      onChange={(e) => setEditForm({...editForm, setting_description: e.target.value})}
                      placeholder="Enter description"
                      rows={2}
                    />
                  </div>
                  <div className="form-actions">
                    <button className="btn-primary" onClick={handleSave}>
                      <Save size={16} />
                      Save
                    </button>
                    <button className="btn-secondary" onClick={handleCancel}>
                      <X size={16} />
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="setting-display">
                  <div className="setting-value">
                    <strong>Value:</strong> {setting.setting_value}
                  </div>
                  {setting.setting_description && (
                    <div className="setting-description">
                      <strong>Description:</strong> {setting.setting_description}
                    </div>
                  )}
                  <div className="setting-meta">
                    <span>Last updated: {new Date(setting.updated_at).toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add Setting Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Setting</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="setting_key">Setting Key *</label>
                <input
                  type="text"
                  id="setting_key"
                  value={addForm.setting_key}
                  onChange={(e) => setAddForm({...addForm, setting_key: e.target.value})}
                  placeholder="e.g., application_fee"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="setting_value">Setting Value *</label>
                <input
                  type="text"
                  id="setting_value"
                  value={addForm.setting_value}
                  onChange={(e) => setAddForm({...addForm, setting_value: e.target.value})}
                  placeholder="Enter value"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="setting_type">Setting Type *</label>
                <select
                  id="setting_type"
                  value={addForm.setting_type}
                  onChange={(e) => setAddForm({...addForm, setting_type: e.target.value})}
                >
                  <option value="string">String</option>
                  <option value="number">Number</option>
                  <option value="boolean">Boolean</option>
                  <option value="json">JSON</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="setting_description">Description</label>
                <textarea
                  id="setting_description"
                  value={addForm.setting_description}
                  onChange={(e) => setAddForm({...addForm, setting_description: e.target.value})}
                  placeholder="Enter description"
                  rows={3}
                />
              </div>
              
              <div className="form-actions">
                <button className="btn-primary" onClick={handleAddSetting}>
                  <Plus size={16} />
                  Add Setting
                </button>
                <button className="btn-secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsManagement; 